import { useState, useEffect, useCallback, useRef } from 'react';
import { listLessons, listPages, getPage } from '../api/assetServiceClient';
import type { LessonListItem, PageListItem, PageDetailOut } from '../types/catalog';

export function useCoursePlayer(courseId: string | undefined) {
  const [lessons, setLessons] = useState<LessonListItem[]>([]);
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [pages, setPages] = useState<PageListItem[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [pageDetail, setPageDetail] = useState<PageDetailOut | null>(null);

  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pageDetailLoading, setPageDetailLoading] = useState(false);
  const [lessonsError, setLessonsError] = useState<string | null>(null);
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [pageDetailError, setPageDetailError] = useState<string | null>(null);

  const lessonsRequestId = useRef(0);
  const pagesRequestId = useRef(0);
  const pageDetailRequestId = useRef(0);

  // Fetch lessons when courseId changes. Do NOT auto-select; parent will call selectLessonAndPage when outline is ready.
  useEffect(() => {
    if (!courseId) {
      setLessons([]);
      setSelectedLessonId(null);
      setPages([]);
      setSelectedPageId(null);
      setPageDetail(null);
      return;
    }
    lessonsRequestId.current += 1;
    const requestId = lessonsRequestId.current;
    setLessonsLoading(true);
    setLessonsError(null);
    listLessons(courseId)
      .then((items) => {
        if (lessonsRequestId.current !== requestId) return;
        setLessons(items);
        setPages([]);
        // Leave selectedLessonId and selectedPageId null; StudentCoursePlayer will initialize from outline + lastPageId
      })
      .catch((err: unknown) => {
        if (lessonsRequestId.current !== requestId) return;
        setLessonsError(err instanceof Error ? err.message : 'Failed to load lessons');
        setLessons([]);
      })
      .finally(() => {
        if (lessonsRequestId.current === requestId) setLessonsLoading(false);
      });
  }, [courseId]);

  // Fetch pages when lesson selected. Preserve selectedPageId if it belongs to this lesson.
  useEffect(() => {
    if (!selectedLessonId) {
      setPages([]);
      setPageDetail(null);
      return;
    }
    pagesRequestId.current += 1;
    const requestId = pagesRequestId.current;
    setPagesLoading(true);
    setPagesError(null);
    listPages(selectedLessonId)
      .then((items) => {
        if (pagesRequestId.current !== requestId) return;
        setPages(items);
        setSelectedPageId((prev) => {
          if (prev && items.some((p) => p.id === prev)) return prev;
          return items[0]?.id ?? null;
        });
        setPageDetail((prev) => {
          // Clear pageDetail only if current page is not in this lesson
          if (!prev) return null;
          if (items.some((p) => p.id === prev.id)) return prev;
          return null;
        });
      })
      .catch((err: unknown) => {
        if (pagesRequestId.current !== requestId) return;
        setPagesError(err instanceof Error ? err.message : 'Failed to load pages');
        setPages([]);
      })
      .finally(() => {
        if (pagesRequestId.current === requestId) setPagesLoading(false);
      });
  }, [selectedLessonId]);

  // Fetch page detail when page selected. Ignore stale responses.
  useEffect(() => {
    if (!selectedPageId) {
      setPageDetail(null);
      return;
    }
    pageDetailRequestId.current += 1;
    const requestId = pageDetailRequestId.current;
    setPageDetailLoading(true);
    setPageDetailError(null);
    getPage(selectedPageId)
      .then((data) => {
        if (pageDetailRequestId.current !== requestId) return;
        setPageDetail(data);
      })
      .catch((err: unknown) => {
        if (pageDetailRequestId.current !== requestId) return;
        setPageDetailError(err instanceof Error ? err.message : 'Failed to load page');
        setPageDetail(null);
      })
      .finally(() => {
        if (pageDetailRequestId.current === requestId) setPageDetailLoading(false);
      });
  }, [selectedPageId]);

  const selectLesson = useCallback((lessonId: string | null) => {
    setSelectedLessonId(lessonId);
  }, []);

  const selectPage = useCallback((pageId: string | null) => {
    setSelectedPageId(pageId);
  }, []);

  const selectLessonAndPage = useCallback((lessonId: string, pageId: string) => {
    setSelectedLessonId(lessonId);
    setSelectedPageId(pageId);
  }, []);

  const currentPageIndex = pages.findIndex((p) => p.id === selectedPageId);

  const nextPage = useCallback(() => {
    if (currentPageIndex >= 0 && currentPageIndex < pages.length - 1) {
      setSelectedPageId(pages[currentPageIndex + 1].id);
    }
  }, [pages, currentPageIndex]);

  const prevPage = useCallback(() => {
    if (currentPageIndex > 0) {
      setSelectedPageId(pages[currentPageIndex - 1].id);
    }
  }, [pages, currentPageIndex]);

  const hasNextPage = currentPageIndex >= 0 && currentPageIndex < pages.length - 1;
  const hasPrevPage = currentPageIndex > 0;

  return {
    lessons,
    selectedLessonId,
    pages,
    selectedPageId,
    pageDetail,
    lessonsLoading,
    pagesLoading,
    pageDetailLoading,
    lessonsError,
    pagesError,
    pageDetailError,
    selectLesson,
    selectPage,
    selectLessonAndPage,
    nextPage,
    prevPage,
    hasNextPage,
    hasPrevPage,
  };
}
